// src/components/skills/SkillCard.tsx
//
// Card de skill da listagem /skills — markup LITERAL do wireframe §08
// (.sklc/.sklc-h/.sklc-ic/.skl-badges/.sklc-desc/.sklc-foot + kebab .skl-kebab,
// varredura v2 §5.6-C3). CSS: src/styles/wf-skills.css.
//
// C2: Steps e % Sucesso REAIS — backend retorna steps_count e success_rate
// (0-1) na lista (api.ts Skill). null/undefined → "—" (degrade pré-restart).

import type { Skill } from '@/lib/api';
import { Sparkles, Eye, Play, Upload, Download, Share2, Trash2 } from 'lucide-react';
import { fmtNumber } from '@/lib/format';
import { WfKebabMenu, type WfKebabEntry } from '@/components/ui/WfKebabMenu';
import { STATUS_LABELS, formatSkillSuccessRate } from './skill-constants';

interface SkillCardProps {
  skill: Skill;
  onClick: (skill: Skill) => void;
  /** Suprime o click do card logo após fechar o menu (ver SkillsPage). */
  onMenuOpenChange?: (open: boolean) => void;
  onViewDetails: () => void;
  onDispatch: () => void;
  onPromote: () => void;
  onInstantiate: () => void;
  onDelete: () => void;
}

export function SkillCard({
  skill,
  onClick,
  onMenuOpenChange,
  onViewDetails,
  onDispatch,
  onPromote,
  onInstantiate,
  onDelete,
}: SkillCardProps) {
  // status vem no payload da lista mas não está no type Skill (api.ts intocável).
  const status = (skill as unknown as { status?: string }).status;
  const isGlobal = skill.scope === 'global';

  const menuItems: WfKebabEntry[] = [
    { key: 'details', label: 'Ver detalhes', icon: Eye, onSelect: onViewDetails },
    { key: 'dispatch', label: 'Disparar skill', icon: Play, onSelect: onDispatch },
    isGlobal
      ? { key: 'instantiate', label: 'Instanciar no tenant', icon: Download, onSelect: onInstantiate }
      : { key: 'promote', label: 'Promover para global', icon: Upload, onSelect: onPromote },
    'sep',
    { key: 'share', label: 'Compartilhar', icon: Share2, soon: true, title: 'Em breve' },
    { key: 'delete', label: 'Excluir skill', icon: Trash2, danger: true, onSelect: onDelete },
  ];

  return (
    <div
      className="sklc"
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a, [role="menu"]')) return;
        onClick(skill);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(skill);
        }
      }}
      aria-label={`Skill ${skill.name || skill.slug}`}
    >
      {/* Kebab de ações (⋮) — wireframe .skl-kebab */}
      <WfKebabMenu
        ariaLabel={`Ações da skill ${skill.slug}`}
        items={menuItems}
        onOpenChange={onMenuOpenChange}
      />

      {/* Header: ícone + nome + slug · versão + badges (wireframe .sklc-h) */}
      <div className="sklc-h">
        <div className="sklc-ic">
          <Sparkles className="ic" aria-hidden />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sklc-name">{skill.name || skill.slug}</div>
          <div className="sklc-slug">
            {skill.slug} · v{skill.version}
          </div>
          <div className="skl-badges">
            <span className="skl-bdg ver">v{skill.version}</span>
            <span className={`skl-bdg ${isGlobal ? 'glob' : 'tnt'}`}>{skill.scope}</span>
            {status && status !== 'published' && (
              <span className="skl-bdg">{STATUS_LABELS[status] ?? status}</span>
            )}
          </div>
        </div>
      </div>

      {/* Descrição */}
      <p className="sklc-desc">{skill.description || 'Sem descrição.'}</p>

      {/* Métricas: Steps · Execuções · Sucesso (C2 — dados reais da lista) */}
      <div className="sklc-foot">
        <span>
          Steps <b>{skill.steps_count != null ? fmtNumber(skill.steps_count) : '—'}</b>
        </span>
        <span>
          Execuções <b>{fmtNumber(skill.total_executions ?? 0)}</b>
        </span>
        <span>
          Sucesso <b>{formatSkillSuccessRate(skill.success_rate)}</b>
        </span>
      </div>
    </div>
  );
}
